import { Injectable, signal, WritableSignal } from "@angular/core";
import {
  DataService as CoreDataService,
  ObjectKey,
  ObjectSpaceService,
  ObjectTypeInfo,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  ReadListDataSourceModel,
  TargetColumnValue,
  TargetObjectData,
} from "@barthauer/abc/core/data";
import { ResourceManager } from "@barthauer/abc/core/resources";
import { UserService } from "@barthauer/basys/core/authentication";
import { Address } from "@barthauer/custom/arena";
import {
  Department,
  DocumentKind,
  GeoDsDocument,
  Measure,
  Person,
} from "@barthauer/custom/xmt-models";
import config from "devextreme/core/config";
import { exportDataGrid as exportExcelDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportPdfDataGrid } from "devextreme/pdf_exporter";
import repaintFloatingActionButton from "devextreme/ui/speed_dial_action/repaint_floating_action_button";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from "rxjs";
import { catchError, first, map, switchMap, tap } from "rxjs/operators";
import {
  ActionType,
  NotificationMessage,
  PersonStatus,
  ROLES,
  ToastType,
} from "../models/enumeration-list.model";

import { AppService } from "./app.service";
import { NotificationService } from "./notification.service";
import {
  AccessRights,
  ISkipProfileSelectionParams,
} from "../models/interfaces.model";
import {
  AbcConfigService,
  AbcConfiguration,
} from "@barthauer/abc/core/configuration";
import { NotificationSettingType } from "./notification1.service";
import { UserRolePermissionViewModel } from "../view-models/user-role-permission.view-model";
import { DefinitionsEditorService } from "libs/abc/queries/parameters/definitions-editor/src/lib/definitions-editor.service";
export enum ObjectKeys {
  ADDRESS = "Address",
  MAILADDRESS = "MailAddress",
  PHONENUMBER = "PhoneNumber",
  COLLABORATOR = "xmtMeasureTaskCollaborator",
  DOCUMENT = "Document",
  EVENT = "xmtSubProjectEvent",
  MEASURE = "xmtMeasure",
  MEASUREPROGRESS = "xmtMeasureProgress",
  MEASURETASK = "xmtMeasureTask",
  PERSON = "Person",
  PICKLISTITEM = "PicklistItem",
  RESOURCE = "xmtMeasureResource",
  RESPONSIBLEPLAN = "xmtResponsiblePlan",
  SUBPROJECT = "xmtSubProject",
  PROJECT = "Project",
  STAFFMEMBER = "xmtStaffMember",
  TAG = "xmtMeasureTag",
  TASK = "xmtMeasureTask",
  SUPPORTTASK = "Task",
  DEPARTMENT = "Department",
  ADDRESSPERSON = "AddressPerson",
  PROJECT_PERSON = "ProjectPerson",
  COMMENT = "Comment",
  PROPERTY = "Property",
  PICKLIST = "Picklist",
  APPROVAL_REQUEST = "ApprovalRequest",
  APPROVAL_STEP = "ApprovalStep",
  APPROVAL_PROCESS_LIB = "ApprovalProcessLib",
  APPROVAL_STEP_LIB = "ApprovalStepLib",
  REMINDER = "Reminder",
  USER_ROLE_PERMISSION = "xmtUserRolePermission",
}
@Injectable()
export class DataService {
  private measureDetailSubject = new BehaviorSubject<Measure>(null);
  private readonly objectSpaceName = "ABC.Applications.KAOS.Data";
  private gridDataSubject = new Subject<any>();
  private uploadData: ReplaySubject<GeoDsDocument> = new ReplaySubject();
  public updateGridData$ = this.gridDataSubject.asObservable();
  public uploadData$: Observable<GeoDsDocument> =
    this.uploadData.asObservable();
  public cachedResponsiblesResponse: Person[] = [];
  public cachedResponsiblesResponseWithImages: Person[] = [];
  public cachedProfileAvatar: string[] = [];
  public cachedCompany: Address[] = [];
  public cachedDepartement: Department[] = [];
  public favoriteMeasureId = new Subject<string>();
  public addressPersonId: string;
  public favoriteMeasureId$ = this.favoriteMeasureId.asObservable();
  public res = ResourceManager.getResources("ubt-geods");
  public returnPath: string;
  public profilId: string;
  public ROLES = ROLES;
  public unothorized = false;
  public isArchived = signal(false);
  private readonly resetPasswordApi: string;
  private accessMap = new Map<string, AccessRights>();
  private redirectParameters: ISkipProfileSelectionParams = {};
  public currentCongfiguration: AbcConfiguration;
  public readonly avatarColumns = [
    this.coreDataService.createQueryColumn("Id", "Id"),
    {
      Name: "Object",
      OPath: "Object",
      Format: "Base64Image[300x300]",
    },
  ];

  public userRole: string;
  private rolesLoaded = false;
  private permissionsLoaded = false;
  public userAvatar: string;
  public currentUserName: string;
  public readonly profileColumns = [
    this.coreDataService.createQueryColumn("Id", "Id"),
    this.coreDataService.createQueryColumn("ParentId", "ParentId"),
    this.coreDataService.createQueryColumn("FirstName", "FirstName"),
    this.coreDataService.createQueryColumn("LastName", "LastName"),
    this.coreDataService.createQueryColumn("Function", "Function"),
    this.coreDataService.createQueryColumn("Active"),
  ];
  public readonly adddressPersonQueryColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn("Position", "Position"),
    this.coreDataService.createQueryColumn(
      "Id",
      "Id",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "ParentId",
      "ParentId",
      QueryColumnSortOrder.None,
    ),
  ];
  public permessions: Map<string, boolean> = new Map<string, boolean>();

  public readonly permessionColumuns: QueryColumn[] = [
    this.coreDataService.createQueryColumn(
      "ParentId",
      "ParentId",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Id",
      "Id",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "Designation",
      "permessionName",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "TextValue",
      "module",
      QueryColumnSortOrder.None,
    ),
    this.coreDataService.createQueryColumn(
      "BoolValue",
      "permession",
      QueryColumnSortOrder.None,
    ),
  ];
  private readonly rolePemessionColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn("Id"),
    this.coreDataService.createQueryColumn("IdRefAddress"),
    this.coreDataService.createQueryColumn("Name"),
    this.coreDataService.createQueryColumn("CanCreate"),
    this.coreDataService.createQueryColumn("CanUpdate"),
    this.coreDataService.createQueryColumn("CanDelete"),
    this.coreDataService.createQueryColumn("CanView"),
    this.coreDataService.createQueryColumn("Context"),
  ];

  public isSuperAdmin: boolean | null = null;

  constructor(
    public appService: AppService,
    public coreDataService: CoreDataService,
    private persistenceService: PersistenceService,
    private notificationService: NotificationService,
    private userSevice: UserService,
    private configService: AbcConfigService,
    private definitionsEditorService: ObjectSpaceService,
  ) {
    this.currentCongfiguration = configService.getConfiguration();
    this.resetPasswordApi =
      this.currentCongfiguration.WebApi.ServiceURLs.ResetPassword;
  }

  prepareUserData = (): Observable<any> => {
    if (!sessionStorage.getItem("profileId") || this.isSuperAdmin === null) {
      return this.userSevice.getCurrentLoginInfo().pipe(
        switchMap((_loginInfo) => {
          sessionStorage.setItem("currentProfile", _loginInfo?.displayName);
          return this.prepareProfile(_loginInfo).pipe(
            switchMap((_profileInfo) => {
              if (Object.keys(_profileInfo).length > 0) {
                sessionStorage.setItem("profileId", _profileInfo[0].Id);
                this.profilId = _profileInfo[0].Id;

                return this.prepareProfileAvatar(_profileInfo[0].Id).pipe(
                  map((_profileAvatar) => ({
                    _loginInfo,
                    _profileInfo,
                    _profileAvatar,
                  })),
                );
              } else {
                return of([]).pipe(
                  map((_profileAvatar) => ({
                    _loginInfo,
                    _profileInfo,
                    _profileAvatar,
                  })),
                );
              }
            }),
          );
        }),
      );
    } else {
      return of(undefined);
    }
  };

  public loadUserRole(addressId: string, personId: string): Observable<any> {
    if (this.rolesLoaded && this.permissionsLoaded) {
      return of({
        roles: this.userRole,
        permissions: this.accessMap,
      });
    }

    const addressPersonOPath = `ParentId='${addressId}' And And IdRefPerson='${personId}'`;

    return this.readObjects(
      ObjectKeys.ADDRESSPERSON,
      this.adddressPersonQueryColumns,
      addressPersonOPath,
    ).pipe(
      map((roles) => {
        if (roles?.length) {
          this.userRole = roles[0]["Position"];

          this.rolesLoaded = true;
          return roles;
        }
        return null;
      }),
      switchMap((roles) => {
        if (roles && roles.length > 0) {
          this.addressPersonId = roles[0]["Id"];

          return this.loadPermession(roles[0]["Position"]).pipe(
            tap((permissions) => {
              this.permissionsLoaded = true;
              this.buildAccessMap(permissions);
            }),
            map((permissions) => ({
              roles: this.userRole,
              permissions: this.accessMap,
            })),
          );
        }
        return of({ roles: this.userRole, permissions: new Map() });
      }),
      catchError((error) => {
        console.error(
          "Error loading user roles or fetching permissions:",
          error,
        );
        return of({ roles: [], permissions: [] });
      }),
    );
  }

  public getPermissionsForRole(
    roleName: string,
  ): Observable<Map<string, AccessRights>> {
    if (this.accessMap.size > 0) {
      return of(this.accessMap);
    }

    return this.loadPermession(roleName).pipe(
      map((permissionsByModule) => {
        this.buildAccessMap(permissionsByModule);

        return this.accessMap;
      }),
      catchError((error) => {
        console.error("Error fetching permissions:", error);
        return of(new Map<string, AccessRights>());
      }),
    );
  }

  public loadPermession = (
    roleName: string,
    IdRefAddress?: string,
  ): Observable<UserRolePermissionViewModel[]> => {
    const addressId: string =
      IdRefAddress ?? sessionStorage.getItem("profileAddressId") ?? "";

    const rolePermessionQuery: Query = new Query();
    rolePermessionQuery.ObjectType = ObjectKeys.USER_ROLE_PERMISSION;
    rolePermessionQuery.Columns = this.rolePemessionColumns;
    rolePermessionQuery.OPath = `IdRefAddress='${addressId}' And Name='${roleName}'`;

    const nestedPermessionQuery: Query = new Query();
    nestedPermessionQuery.Name = "ChildUserRolePermissions";
    nestedPermessionQuery.ObjectType = ObjectKeys.USER_ROLE_PERMISSION;
    nestedPermessionQuery.OPath = "ChildUserRolePermissions";
    nestedPermessionQuery.Columns = [
      this.coreDataService.createQueryColumn("Id"),
      this.coreDataService.createQueryColumn("IdRefAddress"),
      this.coreDataService.createQueryColumn("Name"),
      this.coreDataService.createQueryColumn("Context"),
      this.coreDataService.createQueryColumn("CanCreate"),
      this.coreDataService.createQueryColumn("CanDelete"),
      this.coreDataService.createQueryColumn("CanView"),
    ];

    rolePermessionQuery.ObjectQueries = [nestedPermessionQuery];

    const query: QueryObjectsModel = new QueryObjectsModel();
    query.ObjectQueries = [rolePermessionQuery];

    return this.coreDataService.executeReadObjectsQuery(query).pipe(
      first(),
      map((data) => {
        if (Array.isArray(data) && data.length > 0) {
          return data;
        }
        return [];
      }),
    );
  };

  private buildKey(parent: string, child?: string): string {
    return child ? `${parent}-${child}` : parent;
  }
  public buildAccessMap(
    data: UserRolePermissionViewModel[] | null | undefined,
  ): void {
    this.accessMap.clear();

    if (!data || data.length === 0) {
      return;
    }

    for (const parent of data) {
      if (!parent?.Context) continue;

      this.accessMap.set(this.buildKey(parent.Context), {
        Create: !!parent.CanCreate,
        Update: !!parent.CanUpdate,
        Delete: !!parent.CanDelete,
        View: !!parent.CanView,
      });

      if (Array.isArray(parent.ChildUserRolePermissions)) {
        for (const child of parent.ChildUserRolePermissions) {
          if (!child?.Context) continue;

          this.accessMap.set(this.buildKey(parent.Context, child.Context), {
            Create: !!child.CanCreate,
            Update: !!child.CanUpdate,
            Delete: !!child.CanDelete,
            View: !!child.CanView,
          });
        }
      }
    }
  }
  public hasPermission(context: string, action: keyof AccessRights): boolean {
    if (this.isSuperAdmin) return true;
    return this.accessMap.get(context)?.[action] ?? false;
  }

  public checkPermissionForAdministrationMenu(modules: string[]): boolean {
    if (!modules || modules.length === 0) {
      return true;
    }

    return modules.some((module) => this.hasPermission(module, ActionType.SEE));
  }

  public getBasePath(path: string): string {
    const cleanedPath = path.replace(/^\//, "");

    const basePath = cleanedPath.split("/:")[0].split("/")[0];

    return basePath;
  }

  public hasNoMatchingRole(roles: string[]): boolean {
    return !roles.some((role) => role === this.userRole);
  }
  public getRole(): string {
    return this.isSuperAdmin ? ROLES.SUPER_ADMIN : (this.userRole ?? "");
  }
  public setRole(role: string) {
    this.userRole = role;
  }

  public hasAnyRole(roles: string[]): boolean {
    if (this.isSuperAdmin) return true;
    return roles.some((role) => role === this.userRole);
  }
  public clearRoles(): void {
    this.userRole = "";
    this.rolesLoaded = false;
    this.permissionsLoaded = false;
    this.permessions = new Map<null, null>();
  }
  public getAllPermessions(): Map<string, boolean> {
    return this.permessions;
  }

  private prepareProfile(userName) {
    const profileOPath = `Login='${userName.userName}'`;
    return this.readObjects(
      ObjectKeys.PERSON,
      this.profileColumns,
      profileOPath,
    );
  }

  private prepareProfileAvatar(profileId: string) {
    const profileAvatarOPath = "ParentId=" + "'" + profileId + "'";
    return this.readObjects(
      ObjectKeys.DOCUMENT,
      this.avatarColumns,
      profileAvatarOPath,
    );
  }
  public setProfileInformations = (profileInfo): void => {
    this.userAvatar = profileInfo._profileAvatar[0]?.Object;
    if (profileInfo._loginInfo) {
      this.currentUserName =
        profileInfo._loginInfo?.displayName || profileInfo._loginInfo?.userName;
    } else {
      this.currentUserName = sessionStorage.getItem("currentProfile");
      this.userAvatar = sessionStorage.getItem("userAvatar");
    }
  };
  public readObjects<T>(
    objectType: string,
    columns: QueryColumn[],
    OPath?: string,
  ): Observable<any> {
    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = objectType;
    mainQuery.Columns = columns;
    mainQuery.OPath = OPath;
    if (OPath) {
      mainQuery.OPath = OPath;
    }
    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }

  public deleteObject(
    objectKey: ObjectKeys,
    id: string | number,
  ): Observable<any> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    persistQuery.ObjectSpaceName = this.objectSpaceName;
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Delete;
    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id.toString();
    targetObject.ObjectKey.ObjectType = objectKey;
    persistQuery.Object = targetObject;
    return this.persistenceService.executePersistObjectQuery(persistQuery);
  }

  public updateGridData(data: any) {
    this.gridDataSubject.next(data);
  }

  public getMeasureDetail(): Observable<Measure> {
    return this.measureDetailSubject.asObservable();
  }

  public sendMeasureDetail(measureAssessment: Measure) {
    this.measureDetailSubject.next(measureAssessment);
  }

  public checkGridGroupByColumns(key: string, groupByColumnIndex) {
    if (localStorage.getItem(key)) {
      const gridState = JSON.parse(localStorage.getItem(key));
      gridState.columns.map((item) => {
        if (item.groupIndex) {
          groupByColumnIndex[item.dataField] = item.groupIndex;
          return groupByColumnIndex;
        }
      });
    }
  }
  public prepareStaffMembersWithDocuments(): QueryObjectsModel {
    const personColumns = [
      this.coreDataService.createQueryColumn(
        "Id",
        "Id",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "FirstName",
        "FirstName",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "LastName",
        "LastName",
        QueryColumnSortOrder.None,
      ),
    ];

    const staffMemberOpath =
       `Not(LastName = 'Admin') And Exists(AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}'])`;
    const person: Query = new Query();
    person.ObjectType = ObjectKeys.PERSON;
    person.OPath = staffMemberOpath;
    person.Columns = personColumns;

    const addressPerson: Query = new Query();
    addressPerson.ObjectType = ObjectKeys.ADDRESSPERSON;
    addressPerson.Name = "AddressPerson";
   addressPerson.OPath = `AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}']`;

    addressPerson.Columns = this.adddressPersonQueryColumns;

    person.ObjectQueries = [addressPerson];
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [person];
    return queryDocument;
  }

  public prepareStaffMemberList(): QueryObjectsModel {
    const person: Query = new Query();
    person.ObjectType = ObjectKeys.PERSON;

    const columns = [
      this.coreDataService.createQueryColumn(
        "Id",
        "Id",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "FirstName",
        "FirstName",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "LastName",
        "LastName",
        QueryColumnSortOrder.None,
      ),
    ];

    person.Columns = columns;

    person.OPath = `Not(LastName = 'Admin') And Exists(ProjectPersons[ParentId='${sessionStorage.getItem("projectId")}']) And Exists(AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}']) AND (AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}'].Status <> '${PersonStatus.ARCHIVED}' AND Active <> 0)`;

    const addressPerson: Query = new Query();
    addressPerson.ObjectType = ObjectKeys.ADDRESSPERSON;
    addressPerson.Name = "AddressPerson";
    addressPerson.OPath = `AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}']`;

    addressPerson.Columns = this.adddressPersonQueryColumns;

    person.ObjectQueries = [addressPerson];

    const personDetailsColumns = new QueryObjectsModel();
    personDetailsColumns.ObjectQueries = [person];

    return personDetailsColumns;
  }

  public prepareStaffMembers(): Observable<any> {
    return this.coreDataService.executeReadObjectsQuery(
      this.prepareStaffMemberList(),
    );
  }

  public getDataType(dataType) {
    switch (dataType) {
      case "String":
        return "string";
      case "Integer32":
        return "number";
      case "Date":
        return "date";
      default:
        return "string";
    }
  }
  public readonly documentbyIdColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn("Id", "Id"),
    this.coreDataService.createQueryColumn("Object", "Object"),
  ];

  public previewDocument = (document) => {
    const documentId = document.Id;
    const _documentOpath = `Id = '${documentId}'`;
    const documentDesignation = document.Designation;
    const pdfViewer = window.open("");
    pdfViewer.document.write("<title>" + documentDesignation + "</title>");
    this.readObjects(
      ObjectKeys.DOCUMENT,
      this.documentbyIdColumns,
      _documentOpath,
    ).subscribe(
      (_document) => {
        pdfViewer.document.write(
          `<iframe width='100%' height='100%' src='data:${this.getMimeType(document)};base64,${encodeURI(
            _document[0].Object,
          )}'></iframe>`,
        );
      },
      (error) => {
        console.error(error);
      },
    );
  };

  public getResponsibleFullName(account: Person): string {
    return account ? account?.LastName + ", " + account?.FirstName : "";
  }

  public customizePercentageText(event) {
    return event ? event + "%" : 0 + "%";
  }

  public loadListDataSource(
    objectType: string,
    columnName: string,
  ): Observable<any> {
    const readListDataSourceQuery = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType;
    readListDataSourceQuery.ObjectKey = objectKey;
    readListDataSourceQuery.OutputMode = 0;
    readListDataSourceQuery.ColumnName = columnName;
    return this.coreDataService.executeReadListDataSource(
      readListDataSourceQuery,
    );
  }

  public persistUploadDocument(document: GeoDsDocument): void {
    const documentPersistQuery: TargetObjectData = new TargetObjectData();
    documentPersistQuery.ObjectKey = new ObjectKey();
    documentPersistQuery.ObjectKey.ObjectType = ObjectKeys.DOCUMENT;
    const documentColumns: TargetColumnValue[] = [
      { Name: "Object", Value: this.getBinaryStringFromUrl(document?.Object) },
      { Name: "Designation", Value: document?.Designation },
      { Name: "FileName", Value: document?.FileName },
      { Name: "ParentId", Value: document?.ParentId },
      { Name: "Kind", Value: document?.Kind },
      { Name: "ParentObjectType", Value: document?.ParentObjectType },
    ];
    if (document?.Id !== undefined && document?.Id !== null) {
      documentPersistQuery.ObjectKey.Id = document?.Id;
      documentPersistQuery.Mode = PersistMode.Update;
    } else {
      documentPersistQuery.Mode = PersistMode.Insert;
    }
    documentPersistQuery.TargetColumns = documentColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = documentPersistQuery;
    this.persistenceService.executePersistObjectQuery(persistObject).subscribe(
      (data) => {
        this.updateGridData(data);
        this.notificationService.callNotification({
          message: NotificationMessage.ValidUploadMessage,
          type: ToastType.SUCCESS,
        });
      },
      (error) => {
        this.notificationService.callNotification({
          message: NotificationMessage.Error,
          type: ToastType.ERROR,
        });
      },
    );
  }

  private getBinaryStringFromUrl(url: string): string {
    const img = url?.split(",")[1];
    if (img) {
      return img;
    } else {
      return url;
    }
  }

  public onFileChanged(event, document: GeoDsDocument): void {
    let documentSource = "";
    let fileName = "";
    if (event?.value !== null) {
      const file = event?.value[0];
      if (file?.name !== undefined) {
        fileName = file.name;
      }
      const reader = new FileReader();
      reader.readAsDataURL(new Blob([file]));
      reader.onloadend = () => {
        documentSource = reader.result as string;
        document.Object = documentSource;
        document.FileName = fileName;
        this.uploadData.next(document);
      };
    }
  }

  public downloadFile(doc: GeoDsDocument) {
    const a = document.createElement("a");
    a.href = `data:${this.getMimeType(doc)};base64,${doc.Object}`;
    a.download = doc.FileName;
    a.click();
  }

  private getMimeType(doc: GeoDsDocument, ext?: string) {
    const _extension = doc.FileName.split(".").pop();
    const extension = (ext || _extension).toLowerCase();
    switch (extension) {
      case "png":
        return "image/png";
      case "jpg":
      case "JPG":
        return "image/jpeg";
      case "pdf":
      case "zip":
        return "application/" + extension;
      case "doc":
        return "application/msword";
      case "docx":
        return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      case "ppt":
        return "application/vnd.ms-powerpoint";
      case "pptx":
        return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
      case "txt":
        return "text/plain";
      case "xls":
        return "application/vnd.ms-excel";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      case "csv":
        return "text/csv";
      default:
        const fileNameExt = doc.FileName?.split(".").pop()?.toLowerCase();
        if (fileNameExt && fileNameExt !== extension) {
          return this.getMimeType(doc, fileNameExt);
        } else if (doc.FileName?.indexOf(".") === -1) {
          if (doc.Kind) {
            switch (doc.Kind) {
              case DocumentKind.Bild:
                return "image/jpeg";
              default:
                return "application/octet-stream";
            }
          }
        }
        return "application/octet-stream";
    }
  }

  public floatingButtonConfig() {
    config({
      floatingActionButtonConfig: {
        icon: "rowfield",
        shading: true,
        direction: "down",
        position: {
          of: "#grid",
          my: "right bottom",
          at: "right bottom",
          offset: "-16 -16",
        },
      },
    });
    repaintFloatingActionButton();
  }

  loadUserByPermessionList(permission: string): Observable<
    {
      Id: string;
      fullName: string;
      EmailNotification: number;
      InAppNotification: number;
      Language: string;
    }[]
  > {
    const companyId = sessionStorage.getItem("profileAddressId");
    const permessionOpath = `Context = '${permission}' and IdRefAddress ='${companyId}' And CanView = true `;
    const permessionQueryColumns: QueryColumn[] = [
      this.coreDataService.createQueryColumn("Context"),
      this.coreDataService.createQueryColumn("Name"),
    ];
    const queryColumns: QueryColumn[] = [
      this.coreDataService.createQueryColumn(
        "IdRefPerson",
        "Id",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "ref:IdRefPerson",
        "fullName",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        `iif(Exists(Person.Properties[Designation = '${NotificationSettingType.EMAIL}']),Person.Properties[Designation = '${NotificationSettingType.EMAIL}'].BoolValue <>   false, true)`,
        "EmailNotification",
      ),
      this.coreDataService.createQueryColumn(
        `iif(Exists(Person.Properties[Designation = '${NotificationSettingType.EMAIL}']),Person.Properties[Designation = '${NotificationSettingType.EMAIL}'].TextValue)`,
        "Language",
      ),
      this.coreDataService.createQueryColumn(
        `iif(Exists(Person.Properties[Designation = '${NotificationSettingType.INAPP}']),Person.Properties[Designation = '${NotificationSettingType.INAPP}'].BoolValue <>   false, true)`,
        "InAppNotification",
      ),
    ];
    return this.readObjects(
      ObjectKeys.USER_ROLE_PERMISSION,
      permessionQueryColumns,
      permessionOpath,
    )
      .pipe(
        switchMap((userRolePermessions) => {
          if (
            Array.isArray(userRolePermessions) &&
            userRolePermessions.length > 0
          ) {
            const roles = userRolePermessions.map((role) => role.Name);
            const userId = sessionStorage.getItem("profileId");
            const opath = `ParentId='${sessionStorage.getItem("projectId")}'    And  Person.Status <> '${PersonStatus.ARCHIVED}' And Person.AddressPerson_IdRefPerson_Person.Position In ('${roles.join("','")}')  And Person.Id <> '${userId}' `;
            return this.readObjects(
              ObjectKeys.PROJECT_PERSON,
              queryColumns,
              opath,
            );
          }
          return of([]);
        }),
      )
      .pipe(
        map(
          (data) =>
            data as {
              Id: string;
              fullName: string;
              EmailNotification: number;
              InAppNotification: number;
              Language: string;
            }[],
        ),
      );
  }

  public generateRedirectionUrl(path: string, params: string[] = []): string {
    let resolvedPath = path;
    // Replace :param placeholders in order
    params.forEach((param) => {
      resolvedPath = resolvedPath.replace(/:[^/]+/, encodeURIComponent(param));
    });

    return `${this.currentCongfiguration.WebApi.BaseURL}/aims/${resolvedPath}`;
  }

  public setRedirectParams = (
    skipProfileParameters: ISkipProfileSelectionParams,
  ): void => {
    this.redirectParameters = skipProfileParameters;
  };

  public getRedirectParams = (): ISkipProfileSelectionParams => {
    return this.redirectParameters;
  };

  public clearRedirectParams = (): void => {
    this.redirectParameters = {};
  };

  public onExporting = (e, fileName: string): void => {
    if (e.format === "xlsx") {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(fileName);
      exportExcelDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            fileName + "." + e.format,
          );
        });
      });
    } else {
      const doc = new jsPDF();
      exportPdfDataGrid({
        jsPDFDocument: doc,
        component: e.component,
        indent: 5,
      }).then(() => {
        doc.save(fileName + "." + e.format);
      });
    }
  };

  public resetPassword(
    login: string,
    password: string,
  ): Observable<ArrayBuffer> {
    const resetAccountPassword = {
      Login: login,
      Password: password,
    };
    return this.coreDataService.executeWebApiPOSTMethod(
      this.resetPasswordApi,
      resetAccountPassword,
    );
  }

  // --------------------------- Persist Object

  public persistObject(
    mode: PersistMode,
    objectKey: ObjectKeys,
    columns?: TargetColumnValue[],
    id?: string,
  ): Observable<ArrayBuffer> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    persistQuery.ObjectSpaceName = this.objectSpaceName;
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = mode;
    targetObject.ObjectKey = new ObjectKey();
    if (mode === PersistMode.Update) {
      targetObject.ObjectKey.Id = id;
    }
    targetObject.ObjectKey.ObjectType = objectKey;
    targetObject.TargetColumns = columns;
    persistQuery.Object = targetObject;
    return this.coreDataService.executePersistObjectQuery(persistQuery);
  }

  public persistMultipleObjects(
    objectsOrKey:
      | { objectKey: ObjectKeys; persistMode: PersistMode; data: any | any[] }[]
      | ObjectKeys,
    persistMode?: PersistMode,
    data?: any[],
  ): Observable<any> {
    const buildObjectData = (
      object: any,
      persistMode: PersistMode,
      objectKey: ObjectKeys,
    ): TargetObjectData => {
      const objectData = new TargetObjectData();
      objectData.Mode = persistMode;
      objectData.ObjectKey = new ObjectKey();
      objectData.ObjectKey.ObjectType = objectKey;

      const queryColumns: TargetColumnValue[] = [];

      if (persistMode === PersistMode.Delete) {
        objectData.ObjectKey.Id = object.Id;
      } else {
        Object.entries(object).forEach(([key, value]) => {
          if (key === "Id") objectData.ObjectKey.Id = value as string;
          queryColumns.push({ Name: key, Value: value });
        });
        objectData.TargetColumns = queryColumns;
      }

      return objectData;
    };

    const query: PersistObjectsModel = new PersistObjectsModel();
    const objectsPersistQuery: TargetObjectData[][] = [[]];

    if (Array.isArray(objectsOrKey)) {
      objectsOrKey.forEach(({ objectKey, persistMode, data }) => {
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((object) => {
            objectsPersistQuery[0].push(
              buildObjectData(object, persistMode, objectKey),
            );
          });
        } else if (data) {
          objectsPersistQuery[0].push(
            buildObjectData(data, persistMode, objectKey),
          );
        }
      });
    } else {
      data?.forEach((object) => {
        objectsPersistQuery[0].push(
          buildObjectData(object, persistMode, objectsOrKey),
        );
      });
    }

    query.Objects = objectsPersistQuery;
    return this.coreDataService.executePersistObjectsQuery(query);
  }
  /**
   * Compares two objects recursively, excluding specified keys.
   * @param obj1 - The first object to compare.
   * @param obj2 - The second object to compare.
   * @param excludeKeys - Keys to exclude from comparison.
   * @returns True if objects are different, false otherwise.
   */
  public compareObjects(
    obj1: Object,
    obj2: Object,
    excludeKeys: string[] = [],
  ): boolean {
    const keys1 = Object.keys(obj1).filter((key) => !excludeKeys.includes(key));

    for (const key of keys1) {
      const v1 = obj1[key];
      const v2 = obj2[key];

      if (v1 !== v2) {
        if (
          typeof v1 === "object" &&
          typeof v2 === "object" &&
          v1 !== null &&
          v2 !== null
        ) {
          if (JSON.stringify(v1) !== JSON.stringify(v2)) {
            return true;
          }
        } else {
          return true;
        }
      }
    }

    return false;
  }
  public getIdentifier(prefix: string, date: string) {
    return prefix + Date.parse(date);
  }

  public prepareDate(date: Date = new Date()): Date {
    return new Date(
      new Date(
        date.getFullYear() +
          "-" +
          (date.getMonth() + 1) +
          "-" +
          date.getDate() +
          " " +
          date.getHours() +
          ":" +
          date.getMinutes() +
          ":" +
          date.getSeconds() +
          ".000Z",
      ),
    );
  }

  /**
   * Verify if passed var null or empty
   * @param data accept var of any type and verify after that if it's null or empty string
   * @returns true if null or empty, false otherwise
   */
  public isNullOrEmpty(data: any): boolean {
    return (
      data === null ||
      data === undefined ||
      (typeof data === "string" && data === "")
    );
  }

  /**
   * Formats a given date value to a string depending on the application language
   * with two-digit day and month.
   *
   * @param {Date} date The date object to format.
   * @returns {string} The formatted date.
   */
  public formatDate(date: string, includeTime: boolean = false): string {
    if (!this.isNullOrEmpty(date)) {
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };

      if (includeTime) {
        options.hour = "2-digit";
        options.minute = "2-digit";
      }

      return new Date(date).toLocaleString(this.res("dateFormat"), options);
    }

    return "";
  }

  /**
   * Formats a person's name based on the current application language
   * stored in sessionStorage as 'currentUserLanguage'.
   *
   * For English ('en'): FirstName LastName
   * For German ('de'): LastName, FirstName
   *
   * @param firstName - The user's first name
   * @param lastName - The user's last name
   * @returns Formatted full name as a string
   */
  public formatName(firstName: string, lastName: string): string {
    const lang = sessionStorage.getItem("currentUserLanguage").toLowerCase() as
      | "en"
      | "de"
      | null;

    if (!firstName || !lastName) return "";

    switch (lang) {
      case "de":
        return `${firstName} ${lastName}`;
      case "en":
      default:
        return `${firstName} ${lastName}`;
    }
  }

  /**
   * Returns the oPath for the comment Parent based on type
   *
   * @param type - ObjectType
   * @returns opath expression as a string
   */
  public getCommentParentTypeBase(type: ObjectKeys): string {
    return `Type(${type})[Id= ^.Comment.ParentId]`;
  }

  public normalizeEmailText(value: string): string {
    if (!value) return value;

    return (
      value
        // non-breaking space → normal space
        .replace(/\u00A0/g, " ")
        // zero-width chars (Outlook / Word junk)
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        // normalize whitespace
        .replace(/\s+/g, " ")
        .trim()
    );
  }
}
